//==================================================================================================
//                                      FILE UTILITIES
//==================================================================================================
// Utilities for handling file operations, particularly around Medplum Binary resources
// and file type detection.

import { resolveBinaryIdFromUrl as resolveSharedBinaryIdFromUrl } from "@vibrant/models/document/mobile-upload-utils";
import type { IconName } from "@/lib/icons/registry";

/**
 * Determines if a URL is a Medplum Binary resource that needs authentication
 */
export function isMedplumBinaryUrl(url: string): boolean {
  return (
    url.includes("/fhir/R4/Binary/") ||
    url.includes("/Binary/") ||
    // Common FHIR reference form used throughout our resources
    url.startsWith("Binary/")
  );
}

const MEDPLUM_BASE_URL = process.env.NEXT_PUBLIC_MEDPLUM_BASE_URL || "https://api.medplum.com/";
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid({ value }: { value: string }): boolean {
  return UUID_REGEX.test(value);
}

/**
 * Normalizes stored extension / reference values to a Binary resource id (uuid).
 * Handles bare UUIDs, `Binary/<uuid>`, and Medplum URLs that contain `Binary/<uuid>`.
 */
export function normalizeMedplumBinaryId(value: string | undefined | null): string | undefined {
  if (value == null || typeof value !== "string") {
    return undefined;
  }
  // Leading slashes break `^Binary/` matching (common in stored attachment urls)
  const trimmed = value.trim().replace(/^\/+/, "").trim();
  if (!trimmed) {
    return undefined;
  }
  const withoutBinaryPrefix = trimmed.replace(/^Binary\//i, "").trim();
  if (isUuid({ value: withoutBinaryPrefix })) {
    return withoutBinaryPrefix;
  }
  return resolveBinaryIdFromUrl(trimmed);
}

/**
 * Attempts to resolve a Medplum Binary resource id from a URL-like string.
 *
 * Supports:
 * - `Binary/<uuid>`
 * - Any URL that contains `Binary/<uuid>` in its path
 * - Presigned storage URLs (e.g. S3) that embed the Binary id somewhere in the path
 */
export function resolveBinaryIdFromUrl(url: string): string | undefined {
  return resolveSharedBinaryIdFromUrl(url);
}

/**
 * Returns true if a URL is safe to proxy through our `/api/files` endpoint.
 *
 * We intentionally restrict proxying to Medplum-hosted Binary endpoints (or `Binary/<id>` refs)
 * to avoid turning `/api/files` into a general-purpose authenticated fetcher (SSRF risk).
 */
export function isAllowedFilesApiUrl({
  url,
  medplumBaseUrl,
}: {
  url: string;
  medplumBaseUrl: string;
}): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  // Always allow stable FHIR references.
  if (url.startsWith("Binary/")) {
    return true;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return false;
  }

  let allowedOrigin: string;
  try {
    allowedOrigin = new URL(medplumBaseUrl).origin;
  } catch {
    return false;
  }

  if (parsed.origin !== allowedOrigin) {
    return false;
  }

  // Defense-in-depth: only allow Binary endpoints, not arbitrary Medplum URLs.
  return parsed.pathname.includes("/Binary/");
}

function buildFilesApiUrl({
  binaryId,
  url,
  filename,
  inline,
}: {
  binaryId?: string;
  url?: string;
  filename?: string | null;
  inline?: boolean;
}): string {
  const searchParams = new URLSearchParams();
  if (binaryId) {
    searchParams.set("binaryId", binaryId);
  } else if (url) {
    searchParams.set("url", url);
  }

  if (filename) {
    searchParams.set("filename", filename);
  }
  if (inline !== undefined) {
    searchParams.set("inline", inline ? "true" : "false");
  }

  return `/api/files?${searchParams.toString()}`;
}

/**
 * Gets the appropriate URL for accessing a file, proxying Medplum Binary URLs through our API
 */
export function getAccessibleFileUrl({
  url,
  binaryId,
  filename,
  inline,
}: {
  url?: string | null;
  binaryId?: string | null;
  filename?: string | null;
  inline?: boolean;
}): string {
  if (!url || typeof url !== "string") {
    if (binaryId && typeof binaryId === "string" && isUuid({ value: binaryId })) {
      return buildFilesApiUrl({ binaryId, filename, inline });
    }
    return "";
  }

  // Only trust a Binary id if we can resolve it from the URL itself.
  // This prevents non-Binary UUIDs (e.g., DiagnosticReport.id) from accidentally overriding a valid URL.
  const resolvedBinaryId = resolveBinaryIdFromUrl(url);
  if (resolvedBinaryId) {
    return buildFilesApiUrl({ binaryId: resolvedBinaryId, filename, inline });
  }

  if (isMedplumBinaryUrl(url)) {
    // Align client-side URL generation with the server-side SSRF allowlist:
    // only proxy URLs that the `/api/files` endpoint will accept.
    if (!isAllowedFilesApiUrl({ url, medplumBaseUrl: MEDPLUM_BASE_URL })) {
      return url;
    }
    try {
      return buildFilesApiUrl({ url, filename, inline });
    } catch (error) {
      console.warn("Failed to encode URL:", error);
      return url;
    }
  }
  return url;
}

/**
 * Gets the appropriate icon for a file based on its content type
 */
export function getFileIcon(contentType: string): IconName<"regular"> {
  if (contentType.startsWith("image/")) return "file-image";
  if (contentType === "application/pdf") return "file-pdf";
  if (contentType.startsWith("video/")) return "file-video";
  if (contentType.startsWith("audio/")) return "file-audio";
  if (contentType.includes("spreadsheet") || contentType.includes("excel")) return "file-excel";
  if (contentType.includes("document") || contentType.includes("word")) return "file-word";
  if (contentType.includes("presentation") || contentType.includes("powerpoint"))
    return "file-powerpoint";
  if (contentType === "text/plain") return "file-text";
  if (contentType.includes("zip") || contentType.includes("archive")) return "file-archive";
  return "file";
}

/**
 * Checks if a content type represents an image file
 */
export function isImageFile(contentType: string): boolean {
  return contentType.startsWith("image/");
}

/**
 * Checks if a content type represents a PDF file
 */
export function isPdfFile(contentType: string): boolean {
  return contentType === "application/pdf";
}

// Raster formats that can't carry script. `image/jpg` is non-standard, but fax attachments use it.
const INLINE_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];

/** How the app may show a file: in an iframe (`pdf`, `text`), as an `<img>`, or only as a download. */
export type FilePreviewMode = "pdf" | "image" | "text" | "none";

/**
 * Decides how a file may render inside the app. `/api/files` serves uploads from our own origin
 * under the content type the uploader chose, so a type that can carry script (HTML, SVG, any other
 * `text/*`) must never open inline — framed or top-level it runs as the viewer. Such files get
 * `none`: link them as a download (`inline: false`), never as an inline preview.
 */
export function getFilePreviewMode(contentType: string): FilePreviewMode {
  const mimeType = getMimeEssence(contentType);
  if (isPdfFile(mimeType)) return "pdf";
  // Allowlist, not `image/*`: SVG and any other image type that can carry script stay a download.
  if (INLINE_IMAGE_TYPES.includes(mimeType)) return "image";
  // Frame plain text only with `sandbox=""`.
  if (mimeType === "text/plain") return "text";
  return "none";
}

/** True for a file whose pages a saved rotation can turn: a PDF or an inline raster image. */
export function canRotateFile(contentType: string): boolean {
  const mode = getFilePreviewMode(contentType);
  return mode === "pdf" || mode === "image";
}

/**
 * True when `/api/files` may serve the file inline. Every other type is forced to a download,
 * whatever the caller asked, so an upload that can carry script never renders on our origin. Audio
 * and video can't run script, so they stay playable in a tab.
 */
export function canServeFileInline(contentType: string): boolean {
  if (getFilePreviewMode(contentType) !== "none") return true;
  const mimeType = getMimeEssence(contentType);
  return mimeType.startsWith("audio/") || mimeType.startsWith("video/");
}

/**
 * The bare `type/subtype` of a Content-Type header. Safety checks must compare this, not the raw
 * header: `image/svg+xml; charset=utf-8` is still SVG. RFC 2045 makes MIME types case-insensitive,
 * so fold case too - otherwise `IMAGE/SVG+XML` slips past the SVG check.
 */
function getMimeEssence(contentType: string): string {
  return (contentType.split(";")[0] ?? "").trim().toLowerCase();
}

/**
 * Builds an inline-preview URL for a PDF shown in an `<iframe>`. Chrome PDFium (and similar) read PDF
 * open parameters from the URL fragment; fit-width + no thumbnail pane makes embedded clinical docs
 * more readable. Pass a URL already made framable (e.g. via {@link getAccessibleFileUrl}).
 */
export function pdfIframePreviewUrl(url: string): string {
  const base = url.split("#")[0] ?? url;
  return `${base}#view=FitH&navpanes=0`;
}

/**
 * Gets a human-readable file type description
 */
export function getFileTypeDescription(contentType: string): string {
  if (contentType.startsWith("image/")) return "Image";
  if (contentType === "application/pdf") return "PDF Document";
  if (contentType.startsWith("video/")) return "Video";
  if (contentType.startsWith("audio/")) return "Audio";
  if (contentType.includes("spreadsheet") || contentType.includes("excel")) return "Spreadsheet";
  if (contentType.includes("document") || contentType.includes("word")) return "Document";
  if (contentType.includes("presentation") || contentType.includes("powerpoint"))
    return "Presentation";
  if (contentType === "text/plain") return "Text File";
  if (contentType.includes("zip") || contentType.includes("archive")) return "Archive";
  return "File";
}
